import { useCallback, useEffect, useRef, useState } from 'react'

import { convertIncomingData } from '../services/transforms'
import { store } from '../stores/RootStore'

/**
 * Check if a JWT token is expired or about to expire
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Consider expired if within this many seconds of expiry
 * @returns {boolean} - True if token is expired or invalid
 */
function isTokenExpired(token, bufferSeconds = 5) {
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const exp = payload.exp * 1000 // Convert to milliseconds
    return Date.now() >= exp - bufferSeconds * 1000
  } catch {
    return true
  }
}

function useWebSocket({ path, onMessage, token, enabled = true, config = {} }) {
  const [isConnected, setIsConnected] = useState(false)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const [isMaxedOut, setIsMaxedOut] = useState(false)
  const reconnectTimeoutRef = useRef(null)
  const isConnectingRef = useRef(false)
  const wsRef = useRef(null)
  const unmountedRef = useRef(false)

  // Refs to avoid stale closures in WebSocket event handlers
  const enabledRef = useRef(enabled)
  const pathRef = useRef(path)
  const onMessageRef = useRef(onMessage)
  const reconnectAttemptsRef = useRef(reconnectAttempts)
  enabledRef.current = enabled
  pathRef.current = path
  onMessageRef.current = onMessage
  reconnectAttemptsRef.current = reconnectAttempts

  const log = useCallback((message, level = 'info') => {
    if (config.debug) {
      // eslint-disable-next-line no-console
      console[level](`[WebSocket] ${message}`)
    }
  }, [])

  const connectionIdRef = useRef(0)

  const cleanupConnection = useCallback(() => {
    isConnectingRef.current = false
    connectionIdRef.current += 1
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
  }, [])

  const connect = useCallback(async () => {
    if (unmountedRef.current || !enabledRef.current || isConnectingRef.current) {
      log('Connection skipped - unmounted, disabled, or already connecting')
      return null
    }

    const maxRetries = config.maxRetries ?? 5
    if (reconnectAttemptsRef.current >= maxRetries) {
      log('Connection rejected - max retries reached')
      setIsMaxedOut(true)
      return null
    }

    if (!pathRef.current) {
      log('Connection rejected - invalid path')
      return null
    }

    cleanupConnection()
    isConnectingRef.current = true
    const myConnectionId = connectionIdRef.current

    // Proactively refresh token if expired before connecting
    if (isTokenExpired(token)) {
      log('Token expired - refreshing before connect', 'warn')
      isConnectingRef.current = false
      try {
        await store.triggerRefreshToken()
        // Token will be updated in store; parent will re-render with new token,
        // which updates the `token` prop and causes the effect to run again.
        return null
      } catch (err) {
        log(`Token refresh failed: ${err}`, 'error')
        return null
      }
    }

    const wsUrl = `${import.meta.env.VITE_WS_BASE_URL}${pathRef.current}/?${
      token ? `token=${token}` : ''
    }`
    log(`Connecting to ${wsUrl}`)
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      log('Connected')
      isConnectingRef.current = false
      setIsConnected(true)
      setReconnectAttempts(0)
      setIsMaxedOut(false)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        onMessageRef.current(convertIncomingData(data))
      } catch (error) {
        log(`Error parsing message: ${error}`, 'error')
      }
    }

    ws.onclose = (event) => {
      log(`Connection closed with code ${event.code}`, 'warn')

      // If a newer connection has been established (cleanup incremented the
      // generation), this is a stale onclose — ignore it entirely.
      if (myConnectionId !== connectionIdRef.current) {
        log('Stale connection closed — skipping reconnect')
        return
      }

      isConnectingRef.current = false
      setIsConnected(false)
      wsRef.current = null

      if (
        !unmountedRef.current &&
        reconnectAttemptsRef.current < maxRetries &&
        enabledRef.current
      ) {
        setReconnectAttempts((prev) => {
          const nextAttempt = prev + 1
          if (nextAttempt >= maxRetries) {
            log('Max retries reached, giving up', 'warn')
            setIsMaxedOut(true)
            return prev
          }

          const delay = Math.min(1000 * 2 ** prev, 10000)
          log(`Scheduling reconnect attempt ${nextAttempt} in ${delay}ms`)

          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, delay)

          return nextAttempt
        })
      }
    }

    ws.onerror = (error) => {
      log(`WebSocket error: ${error}`, 'error')
      isConnectingRef.current = false
    }

    return ws
  }, [log, token, cleanupConnection])

  useEffect(() => {
    unmountedRef.current = false

    if (enabled) {
      connect()
    } else {
      cleanupConnection()
    }

    return () => {
      unmountedRef.current = true
      cleanupConnection()
    }
  }, [enabled, path, connect, cleanupConnection])

  return {
    isConnected,
    reconnectAttempts,
    isMaxedOut,
    disconnect: cleanupConnection,
    reconnect: () => {
      setIsMaxedOut(false)
      setReconnectAttempts(0)
      connect()
    },
    enabled,
  }
}
export { useWebSocket }
